import { Text, View, ScrollView, RefreshControl, Pressable, TouchableOpacity, useWindowDimensions } from 'react-native';
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { useRouter, useNavigation, useLocalSearchParams, usePathname } from 'expo-router';
import { DateTime } from 'luxon';
import { ActivityIndicator } from 'react-native-paper';
import { TabView } from 'react-native-tab-view';

import Chart from '@/components/Charts/Chart';
import AnalyticsDateRangeControls from '@/components/Charts/AnalyticsDateRangeControls';
import PageTitle from '@/components/General/PageTitle';
import DropdownMenu from '@/components/General/DropdownMenu';
import ReachTabBar from '@/components/General/ReachTabBar';
import SaveShortcutModal from '@/components/General/SaveShortcutModal';
import BibleStudySetupNeeded from '@/components/BibleStudies/BibleStudySetupNeeded';
import BibleStudyTeachersPanel from '@/components/BibleStudies/BibleStudyTeachersPanel';
import CutCard from '@/components/General/CutCard';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import usePlatform from '@/utils/hooks/usePlatform';
import useBibleStudies from '@/utils/hooks/useBibleStudies';
import { dayContainer } from '@/utils/reportDates';

const DATE_PRESETS = [
    { label: 'This Week', value: 'this_week' },
    { label: 'Last Week', value: 'last_week' },
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'This Quarter', value: 'this_quarter' },
    { label: 'Last Quarter', value: 'last_quarter' },
    { label: 'Year to Date', value: 'year_to_date' },
    { label: 'This Year', value: 'this_year' },
    { label: 'Last Year', value: 'last_year' },
    { label: 'Past 30 Days', value: 'last_30_days' },
    { label: 'Past 90 Days', value: 'last_90_days' },
    { label: 'Past 12 Months', value: 'last_12_months' },
];

// Dates are day containers: device-local midnight of the org-local calendar day.
const getPresetDates = (preset, timeZone) => {
    const now = timeZone ? DateTime.now().setZone(timeZone) : DateTime.now();
    const day = (dt) => dayContainer(dt);
    switch (preset) {
        case 'this_week':
            return { start: day(now.startOf('week')), end: day(now.endOf('week')) };
        case 'last_week':
            return { start: day(now.minus({ weeks: 1 }).startOf('week')), end: day(now.minus({ weeks: 1 }).endOf('week')) };
        case 'this_month':
            return { start: day(now.startOf('month')), end: day(now.endOf('month')) };
        case 'last_month':
            return { start: day(now.minus({ months: 1 }).startOf('month')), end: day(now.minus({ months: 1 }).endOf('month')) };
        case 'this_quarter':
            return { start: day(now.startOf('quarter')), end: day(now.endOf('quarter')) };
        case 'last_quarter':
            return { start: day(now.minus({ quarters: 1 }).startOf('quarter')), end: day(now.minus({ quarters: 1 }).endOf('quarter')) };
        case 'this_year':
            return { start: day(now.startOf('year')), end: day(now.endOf('year')) };
        case 'last_year':
            return { start: day(now.minus({ years: 1 }).startOf('year')), end: day(now.minus({ years: 1 }).endOf('year')) };
        case 'last_30_days':
            return { start: day(now.minus({ days: 30 })), end: day(now) };
        case 'last_90_days':
            return { start: day(now.minus({ days: 90 })), end: day(now) };
        case 'year_to_date':
            return { start: day(now.startOf('year')), end: day(now) };
        case 'last_12_months':
            return { start: day(now.minus({ months: 12 })), end: day(now) };
        default:
            return null;
    }
};

const SectionHeader = ({ title, subtitle }) => (
    <View style={styles.sectionHeader}>
        <View style={styles.sectionAccent} />
        <View style={styles.sectionHeaderText}>
            <Text style={styles.sectionTitle}>{title}</Text>
            {subtitle ? <Text style={styles.sectionSubtitle}>{subtitle}</Text> : null}
        </View>
    </View>
);

const GroupLabel = ({ children }) => (
    <Text style={styles.groupLabel}>{children}</Text>
);

export default function BibleStudyReportsScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const pathname = usePathname();
    const params = useLocalSearchParams();
    const { isWeb, isNative } = usePlatform();
    const layout = useWindowDimensions();

    const {
        enabled,
        loading,
        canManage,
        canViewReports,
        canViewAllReports,
        setupNeeded,
        roleID,
    } = useBibleStudies();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization } = Query.organization.byId({
        id: loggedInUser?.organizationID,
        options: { enabled: !!loggedInUser?.organizationID },
    });
    const orgTimezone = organization?.timezone;

    // 'all' scope requires 'View Bible Study Reports' or 'Manage Bible Studies';
    // rostered teachers without those stay on 'own'.
    const [scope, setScope] = useState('all');
    const effectiveScope = canViewAllReports ? scope : 'own';
    const showScopeToggle = !!canManage;

    // Top-level tab: report charts vs per-teacher details. Kept in the URL
    // (tab/teacherIDs/period params) so shares + shortcuts restore it.
    const activeTab = params?.tab === 'teachers' ? 'teachers' : 'reports';
    // Route.testID feeds the TabBar item's testID (tab-view v4).
    const tabRoutes = useMemo(() => ([
        { key: 'reports', title: 'Reports', testID: 'bible-study-reports-tab-reports' },
        { key: 'teachers', title: 'Teachers', testID: 'bible-study-reports-tab-teachers' },
    ]), []);
    const tabIndex = activeTab === 'teachers' ? 1 : 0;

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState('last_90_days');
    const [initialized, setInitialized] = useState(false);

    const [shortcutModalOpen, setShortcutModalOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    // Chart refs — ref objects (web Chart mutates ref.current; native forwards
    // the WebView). One ref per embedded chart so pull-to-refresh can reload.
    const chartRefs = useRef(Array.from({ length: 10 }, () => React.createRef()));

    // Flag/permission guard — UI gating only; the report RPCs enforce regardless.
    useEffect(() => {
        if (!loading && (!enabled || !canViewReports)) {
            router.canGoBack() ? router.back() : router.replace("/");
        }
    }, [loading, enabled, canViewReports, router]);

    // Initialize dates/scope from URL params (or the 90-day default the RPCs use).
    useEffect(() => {
        if (initialized || !orgTimezone) return;

        const { preset, startDate: urlStart, endDate: urlEnd, scope: urlScope } = params;

        if (urlScope === 'own' || urlScope === 'all') setScope(urlScope);

        if (preset && preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setSelectedPreset(preset);
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            } else {
                const defaultDates = getPresetDates('last_90_days', orgTimezone);
                setStartDate(defaultDates.start);
                setEndDate(defaultDates.end);
            }
        } else if (urlStart && urlEnd) {
            setSelectedPreset('custom');
            setStartDate(DateTime.fromISO(urlStart).startOf('day').toJSDate());
            setEndDate(DateTime.fromISO(urlEnd).startOf('day').toJSDate());
        } else {
            const defaultDates = getPresetDates('last_90_days', orgTimezone);
            setStartDate(defaultDates.start);
            setEndDate(defaultDates.end);
        }

        setInitialized(true);
    }, [params, initialized, orgTimezone]);

    // Keep the URL shareable/shortcut-able.
    useEffect(() => {
        if (!initialized || !startDate || !endDate) return;

        const newParams = { scope: effectiveScope, teacherID: undefined };
        if (selectedPreset !== 'custom') {
            newParams.preset = selectedPreset;
        } else {
            newParams.startDate = DateTime.fromJSDate(startDate).toISODate();
            newParams.endDate = DateTime.fromJSDate(endDate).toISODate();
        }
        router.setParams(newParams);
    }, [startDate, endDate, selectedPreset, initialized, effectiveScope]);

    const handlePresetChange = (preset) => {
        setSelectedPreset(preset);
        if (preset !== 'custom') {
            const presetDates = getPresetDates(preset, orgTimezone);
            if (presetDates) {
                setStartDate(presetDates.start);
                setEndDate(presetDates.end);
            }
        }
    };

    const handleStartDateChange = (date) => {
        setSelectedPreset('custom');
        setStartDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
    };

    const handleEndDateChange = (date) => {
        setSelectedPreset('custom');
        setEndDate(date ? DateTime.fromJSDate(date).startOf('day').toJSDate() : date);
    };

    const handleScopeChange = (next) => setScope(next);

    const currentUrl = useMemo(() => {
        if (!startDate || !endDate) return pathname;
        const searchParams = new URLSearchParams();
        searchParams.set('scope', effectiveScope);
        if (selectedPreset && selectedPreset !== 'custom') {
            searchParams.set('preset', selectedPreset);
        } else {
            searchParams.set('startDate', DateTime.fromJSDate(startDate).toISODate());
            searchParams.set('endDate', DateTime.fromJSDate(endDate).toISODate());
        }
        // Teachers-tab params are owned by the panel — mirror them into the
        // shortcut URL so a saved shortcut restores the same view.
        if (activeTab === 'teachers') {
            searchParams.set('tab', 'teachers');
            if (params?.teacherIDs) searchParams.set('teacherIDs', params.teacherIDs);
            if (params?.period) searchParams.set('period', params.period);
        }
        return `${pathname}?${searchParams.toString()}`;
    }, [pathname, effectiveScope, selectedPreset, startDate, endDate, activeTab, params?.teacherIDs, params?.period]);

    useEffect(() => {
        const menuOptions = [
            { name: 'Save as Shortcut', onSelect: () => setShortcutModalOpen(true) }
        ];

        navigation.setOptions({
            headerTitle: () => <PageTitle title="Bible Study Reports" />,
            headerRight: () => <DropdownMenu testID="bible-study-reports-menu" options={menuOptions} />
        });
    }, [navigation, currentUrl]);

    const refreshCharts = useCallback(() => {
        setRefreshing(true);
        chartRefs.current.forEach(ref => ref.current?.reload?.());
        setTimeout(() => setRefreshing(false), 800);
    }, []);

    // Chart URL params — scope is always sent; range params apply to
    // range-scoped charts and are ignored by snapshot charts.
    const rangeParams = useMemo(() => {
        const p = { scope: effectiveScope };
        if (startDate) p.startDate = DateTime.fromJSDate(startDate).toISODate();
        if (endDate) p.endDate = DateTime.fromJSDate(endDate).toISODate();
        return p;
    }, [effectiveScope, startDate, endDate]);

    // Date-independent charts get params without dates so a range change
    // doesn't reload their iframe.
    const snapshotParams = useMemo(() => ({
        scope: effectiveScope,
    }), [effectiveScope]);

    if (loading || !initialized) {
        return <View style={styles.loadingContainer}><ActivityIndicator size="large" /></View>;
    }

    if (enabled && setupNeeded) {
        return <BibleStudySetupNeeded canManage={canManage} />;
    }

    const chartHeight = isWeb ? 500 : 400;

    const scopeToggle = showScopeToggle && (
        <View style={styles.scopeRow}>
            {[{ key: 'own', label: 'My Studies' }, { key: 'all', label: 'All Studies' }].map(option => (
                <Pressable
                    key={option.key}
                    testID={`bible-study-reports-scope-${option.key}`}
                    accessibilityRole="button"
                    accessibilityState={{ selected: effectiveScope === option.key }}
                    onPress={() => handleScopeChange(option.key)}
                    style={styles.scopePill(effectiveScope === option.key)}
                >
                    <Text style={styles.scopePillText(effectiveScope === option.key)}>{option.label}</Text>
                </Pressable>
            ))}
        </View>
    );

    const reportsScene = (
            <ScrollView
                contentContainerStyle={styles.screen}
                keyboardShouldPersistTaps="handled"
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={refreshCharts} />}
            >
                {scopeToggle}

                <SectionHeader
                    title="By Date Range"
                    subtitle={effectiveScope === 'own' ? 'Metrics for the studies you teach' : 'Organization-wide metrics for the selected period'}
                />

                <AnalyticsDateRangeControls
                    presets={DATE_PRESETS}
                    selectedPreset={selectedPreset}
                    startDate={startDate}
                    endDate={endDate}
                    onPresetChange={handlePresetChange}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    isNative={isNative}
                    testIDPrefix="bible-study-reports-date-range"
                >
                    <TouchableOpacity
                        style={styles.refreshButton}
                        onPress={refreshCharts}
                        disabled={refreshing}
                        testID="bible-study-reports-refresh"
                        accessibilityRole="button"
                        accessibilityLabel="Refresh reports"
                    >
                        {refreshing ? <ActivityIndicator size={16} /> : <Unicon name="refresh" size={18} colorVariant="primary" />}
                        <Text style={styles.refreshButtonText}>Refresh</Text>
                    </TouchableOpacity>
                </AnalyticsDateRangeControls>

                <GroupLabel>Overview</GroupLabel>
                <View style={styles.chartGrid}>
                    <Chart ref={chartRefs.current[0]} height={chartHeight} autoHeight chartName="bible-studies/OverviewChart" parameters={snapshotParams} />
                    <Chart ref={chartRefs.current[1]} height={chartHeight} autoHeight chartName="bible-studies/SessionsPerWeekChart" parameters={rangeParams} />
                </View>

                <GroupLabel>Outcomes</GroupLabel>
                <View style={styles.chartGrid}>
                    <Chart ref={chartRefs.current[5]} height={chartHeight} autoHeight chartName="bible-studies/SessionsToClosureChart" parameters={rangeParams} />
                </View>

                <GroupLabel>Teachers</GroupLabel>
                <CutCard
                    accentColor="primary"
                    style={styles.linkRow}
                    contentStyle={styles.linkRowContent}
                    onPress={() => router.setParams({ tab: 'teachers' })}
                    testID="bible-study-reports-teachers-link"
                    accessibilityLabel="Teacher details"
                >
                    <Unicon name="people-outline" size={16} colorVariant="primary" />
                    <Text style={styles.linkRowText}>Teacher details</Text>
                    <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                </CutCard>
                <View style={styles.chartGrid}>
                    <Chart ref={chartRefs.current[6]} height={chartHeight} autoHeight chartName="bible-studies/TeacherActivityChart" parameters={rangeParams} />
                    <Chart ref={chartRefs.current[8]} height={chartHeight} autoHeight chartName="bible-studies/TeacherLapseChart" parameters={{ scope: effectiveScope }} />
                </View>

                <View style={{ paddingBottom: 40 }} />
            </ScrollView>
    );

    return (
        <View style={styles.container}>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: tabIndex, routes: tabRoutes }}
                onIndexChange={(index) => router.setParams({ tab: tabRoutes[index]?.key })}
                initialLayout={{ width: layout.width }}
                renderTabBar={(props) => <ReachTabBar {...props} />}
                lazy
                renderScene={({ route }) => route.key === 'teachers'
                    ? <BibleStudyTeachersPanel roleID={roleID} canViewAllReports={canViewAllReports} />
                    : reportsScene}
            />

            <SaveShortcutModal
                isOpen={shortcutModalOpen}
                onClose={() => setShortcutModalOpen(false)}
                personID={loggedInUser?.id}
                screen={currentUrl}
                icon="bar-chart-outline"
                library="ionicon"
                label={selectedPreset && selectedPreset !== 'custom'
                    ? `${DATE_PRESETS.find(d => d.value === selectedPreset)?.label} - Bible Study Reports`
                    : `${DateTime.fromJSDate(startDate).toFormat("MMM dd, yyyy")} to ${DateTime.fromJSDate(endDate).toFormat("MMM dd, yyyy")} - Bible Study Reports`}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    screen: {
        padding: 16,
        gap: 14,
    },
    scopeRow: {
        flexDirection: "row",
        gap: 8,
        paddingBottom: 2,
    },
    scopePill: (selected) => ({
        paddingVertical: 6,
        paddingHorizontal: 14,
        borderRadius: 20,
        backgroundColor: selected ? colors.groupsContainer : colors.surface,
        borderWidth: 1,
        borderColor: selected ? colors.groups : colors.border,
    }),
    scopePillText: (selected) => ({
        fontSize: 12,
        fontFamily: selected ? fonts.bold : fonts.body,
        color: selected ? colors.groups : colors.textSecondary,
    }),
    refreshButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 18,
        backgroundColor: colors.primaryContainer,
        marginBottom: 10,
    },
    refreshButtonText: {
        color: colors.primary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    linkRow: {
        alignSelf: 'flex-start',
        // CutCard owns surface/radius; shadow kept here for web clip-path conversion.
        boxShadow: shadows.card,
        _web: {
            willChange: 'transform',
        },
    },
    linkRowContent: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 16,
    },
    linkRowText: {
        fontSize: 13,
        fontFamily: fonts.heading,
        color: colors.primary,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        marginTop: 10,
    },
    sectionAccent: {
        width: 4,
        alignSelf: 'stretch',
        minHeight: 30,
        borderRadius: 4,
        backgroundColor: colors.primary,
    },
    sectionHeaderText: {
        gap: 2,
    },
    sectionTitle: {
        fontSize: 19,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    sectionSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        paddingBottom: 8,
    },
    groupLabel: {
        fontSize: 12,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginTop: 4,
    },
    chartGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
}));
